import React from 'react'

import { Controller } from 'react-hook-form'

import { Textarea, Toggle } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, object, arrayOf, shape, string, oneOfType, number, func } from 'prop-types'

import { useRole } from 'Pods/ItemPage/TopSection/hooks/useTopSectionProps'
import CustomSelect from 'Pods/Shared/CustomSelect/CustomSelect'
import useFileProcessor from 'Pods/Shared/Hooks/useFileProcessor'

import {
  TECHNOLOGY_OPTIONS,
  MATERIAL_OPTIONS,
  PRINT_RESULT_OPTIONS,
  PRINT_TIME_OPTIONS,
} from '../../../../Utils/Data'
import FileUploadSection from '../../../UseCaseFormSection/Components/FileUploadSection/FileUploadSection'

function EditModeForm({
  control,
  availablePrinterTypes,
  isFetchingPrinterTypes,
  useCase,
  mediaChanges,
  onAddMedia,
  onRemoveExistingMedia,
  onRemoveNewMedia,
  onShowNotification,
}) {
  const role = useRole()
  const { processFiles, isProcessing, progress } = useFileProcessor()

  const handleFilesAdded = (files) => {
    processFiles(
      files,
      () => {},
      (processedFiles) => {
        processedFiles.forEach(file => onAddMedia(file))
      },
      (errorMessage, fileName) => {
        onShowNotification(
          `Error processing file ${fileName || ''}: ${errorMessage}`,
          'error'
        )
      }
    )
  }

  const handleEmbedAdded = (embed) => {
    onAddMedia(embed)
  }

  const existingEmbeds = [
    ...useCase.useCaseEmbeds.filter(embed => !mediaChanges.removed.includes(embed.id)),
    ...mediaChanges.added,
  ]

  return (
    <div className="use-case-media-card__edit">
      <div className="use-cases-area__select-container">
        <span className="use-cases-area__label cgt-body-base-semibold cgt-mb-10">
          3D Printer
        </span>
        <Controller
          name="printerType"
          control={control}
          render={({ field }) => (
            <CustomSelect
              {...field}
              options={availablePrinterTypes}
              placeholder="Select a 3D printer"
              disabled={
                isFetchingPrinterTypes ||
                availablePrinterTypes.length === 0
              }
              isLoading={isFetchingPrinterTypes}
              className="use-cases-area__select"
              searchable
            />
          )}
        />
      </div>

      <div className="use-cases-area__select-container">
        <span className="use-cases-area__label cgt-body-base-semibold cgt-mb-10">
          Material
        </span>
        <Controller
          name="material"
          control={control}
          render={({ field }) => (
            <CustomSelect
              {...field}
              options={MATERIAL_OPTIONS}
              placeholder="Select material"
              className="use-cases-area__select"
              isClearable
            />
          )}
        />
      </div>

      {role !== 'seller' && (
        <div className="use-cases-area__select-container">
          <span className="use-cases-area__label cgt-body-base-semibold cgt-mb-10">
            How did your print turn out?
          </span>
          <Controller
            name="printSuccess"
            control={control}
            render={({ field }) => (
              <CustomSelect
                {...field}
                options={PRINT_RESULT_OPTIONS}
                placeholder="Select result"
                className="use-cases-area__select"
                isClearable={false}
              />
            )}
          />
        </div>
      )}

      <div className="use-cases-area__description-container">
        <span className="use-cases-area__label cgt-body-base-semibold cgt-mb-10">Your comment</span>
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              id="use-case-description-edit"
              placeholder="Share your printing experience"
              maxLength={500}
              textareaClassName="use-cases-area__textarea"
              isPilot
              size="small"
            />
          )}
        />
      </div>

      {/* Media Upload Section */}
      <div className="use-cases-area__file-upload use-cases-area__file-upload--edit-mode">
        <FileUploadSection
          existingMedia={existingEmbeds}
          onRemoveExisting={onRemoveExistingMedia}
          pendingMedia={[]}
          onRemoveMedia={onRemoveNewMedia}
          onFilesAdded={handleFilesAdded}
          onEmbedAdded={handleEmbedAdded}
          onShowNotification={onShowNotification}
          isProcessing={isProcessing}
          progress={progress}
          useCases={[]}
          enableDragAndDrop={false}
        />

        {existingEmbeds.length > 0 && (
          <div className="use-cases-area__media-count">
            {existingEmbeds.length} / 5 media files
          </div>
        )}
      </div>

      <div className="use-cases-area__select-container">
        <span className="use-cases-area__label cgt-body-base-semibold cgt-mb-10">
          Printing technology
        </span>
        <Controller
          name="technology"
          control={control}
          render={({ field }) => (
            <CustomSelect
              {...field}
              options={TECHNOLOGY_OPTIONS}
              placeholder="Select technology"
              className="use-cases-area__select"
              isClearable
            />
          )}
        />
      </div>

      <div className="use-cases-area__select-container">
        <span className="use-cases-area__label cgt-body-base-semibold cgt-mb-10">
          Printing time
        </span>
        <Controller
          name="printTime"
          control={control}
          render={({ field }) => (
            <CustomSelect
              {...field}
              options={PRINT_TIME_OPTIONS}
              placeholder="Select time"
              className="use-cases-area__select"
              isClearable
            />
          )}
        />
      </div>

      <div className="use-cases-area__toggle-container">
        <span className="use-cases-area__label cgt-body-base-semibold">
          Supports used?
        </span>
        <Controller
          name="usedSupport"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Toggle
              id="supports-used-toggle-edit"
              isChecked={value}
              onChange={(e, checked) => onChange(checked)}
            />
          )}
        />
      </div>
    </div>
  )
}

EditModeForm.propTypes = {
  control: object.isRequired,
  availablePrinterTypes: arrayOf(shape({
    label: string.isRequired,
    value: oneOfType([number, string]).isRequired,
    printerTypeId: oneOfType([number, string]),
    printerTypeName: string,
    technology: string,
  })).isRequired,
  isFetchingPrinterTypes: bool.isRequired,
  useCase: shape({
    useCaseEmbeds: arrayOf(shape({
      id: string.isRequired,
      url: string,
      thumbUrl: string,
      mediumUrl: string,
      source: string.isRequired,
    })),
  }).isRequired,
  mediaChanges: shape({
    added: arrayOf(object),
    removed: arrayOf(string),
  }).isRequired,
  onAddMedia: func.isRequired,
  onRemoveExistingMedia: func.isRequired,
  onRemoveNewMedia: func.isRequired,
  onShowNotification: func.isRequired,
}

export default EditModeForm
